import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@bymason-private/auth-provider';
import { Box } from '@bymason-private/legacy-pattern-components';
import { useToast } from '../../';
import { ApiKeysModel, useGetApiKeys } from '@bymason/controller-api-hooks';
import { CreateApiKeyModal } from '../modals/create-api-key-modal';
import { DeleteApiKeyModal } from '../modals/delete-api-key-modal';
import { ApiKeysList } from './api-keys-list';

interface ApiKeysViewProps {
  showCreateApiKeyModal?: boolean;
  onCloseCreateApiKeyModal?: () => void;
  emptyStateComponent?: JSX.Element;
}

export function ApiKeysView({
  showCreateApiKeyModal = false,
  onCloseCreateApiKeyModal,
  emptyStateComponent,
}: ApiKeysViewProps) {
  const { authToken = '' } = useAuth();
  const [setToastMessage, setToastSeverity] = useToast();

  const [getApiKeys, apiKeys, apiKeysError, apiKeysBusy] = useGetApiKeys(authToken);

  const [showDeleteApiKeyModal, setShowDeleteApiKeyModal] = useState(false);
  const [showCreateKeyModal, setShowCreateKeyModal] = useState(false);
  const [apiKeyModalTarget, setApiKeyModalTarget] = useState<ApiKeysModel>();

  const handleModalClose = useCallback(() => {
    setApiKeyModalTarget(undefined);
    setShowCreateKeyModal(false);
    setShowDeleteApiKeyModal(false);
    onCloseCreateApiKeyModal && onCloseCreateApiKeyModal();
  }, [onCloseCreateApiKeyModal]);

  const handleCreateApiKeySuccess = useCallback(() => {
    getApiKeys({});
  }, [getApiKeys]);

  const handleDeleteApiKeySuccess = useCallback(
    (keyName) => {
      setToastSeverity('success');
      setToastMessage(`Sucessfully deleted key "${keyName}"`);
      getApiKeys({});
    },
    [getApiKeys, setToastMessage, setToastSeverity]
  );

  const handleChangeApiKeyError = useCallback(
    (message) => {
      setToastSeverity('error');
      setToastMessage(message);
    },
    [setToastMessage, setToastSeverity]
  );

  const handleDeleteApiKey = (apiKey: ApiKeysModel) => {
    setApiKeyModalTarget(apiKey);
    setShowDeleteApiKeyModal(true);
  };

  useEffect(() => {
    if (showCreateApiKeyModal) {
      setApiKeyModalTarget(undefined);
    }
  }, [showCreateApiKeyModal]);

  useEffect(() => {
    getApiKeys({});
  }, [getApiKeys]);

  useEffect(() => {
    if (apiKeysError && !apiKeysBusy) {
      setToastMessage(apiKeysError);
      setToastSeverity('error');
    }
  }, [apiKeysError, apiKeysBusy, setToastMessage, setToastSeverity]);

  return (
    <Box>
      {(apiKeys?.length || !emptyStateComponent) && (
        <ApiKeysList apiKeys={apiKeys || []} handleDeleteApiKeyModalOpen={handleDeleteApiKey} />
      )}

      {!apiKeysBusy && !apiKeys?.length && emptyStateComponent}

      {(showCreateKeyModal || showCreateApiKeyModal) && (
        <CreateApiKeyModal
          open={showCreateKeyModal || showCreateApiKeyModal}
          onClose={handleModalClose}
          onSuccess={handleCreateApiKeySuccess}
          onError={handleChangeApiKeyError}
        />
      )}

      {showDeleteApiKeyModal && apiKeyModalTarget && (
        <DeleteApiKeyModal
          apiKey={apiKeyModalTarget}
          open={showDeleteApiKeyModal}
          onClose={handleModalClose}
          onSuccess={handleDeleteApiKeySuccess}
          onError={handleChangeApiKeyError}
        />
      )}
    </Box>
  );
}
